import React, { useState } from "react";
import { format } from "date-fns";
import { Link, useLocation } from "react-router-dom";
import toSeoUrl from "@functions/toSeoUrl";
import useTickets from "@context/TicketsContext";
import BookingDetails from "@components/Bookings/BookingDetails";
import SeatIcon from "@components/Icons/SeatIcon";
import InlineMessage from "@components/InlineMessage";
import MoviePoster from "@components/MoviePoster";
import SessionDetails from "@components/SessionDetails";
import VoucherList from "@components/Tickets/VoucherList";
import Button from "@components/Ui/Button";
import Spinner from "@components/Ui/Spinner";
import VoucherLogo from "@components/VoucherManager/VoucherPurchases/VoucherLogo";
import "./style.scss";

const Ticket = ({ ticket }) => {
    const {
        id,
        error,
        errorMessage,
        processing,
        message,
        loading,
        reSendTicketEmail,
    } = useTickets();

    const location = useLocation();

    const defaultDisplayLimit = 8;
    const [emailSent, setEmailSent] = useState(false);
    const sendTicketEmail = (uuid) => {
        reSendTicketEmail(uuid);
        setEmailSent(!emailSent);
    };

    const v2tDetails = ticket?.book_with_cinema;
    const isV2tBooking = ticket?.is_voucher_to_ticket;

    return (
        <>
            <div key={ticket.ticket_id} className="ticket-history__item">
                {ticket.ticket_id && ticket.ticket_type_id === null ? (
                    <div className="ticket-history__img">
                        <MoviePoster
                            movie={ticket}
                            size="small"
                            key={`ticket_img_${ticket.ticket_id}`}
                        />
                    </div>
                ) : (
                    <div className="ticket-history__item--voucher">
                        <VoucherLogo item={ticket.items[0]} />
                    </div>
                )}

                <>
                    {ticket.ticket_id && ticket.ticket_type_id === null ? (
                        <div className="ticket-history__content">
                            <div className="ticket-history__title">
                                <Link
                                    className="ticket-history__link"
                                    to={{
                                        pathname: `/movie/${ticket.movie_id}/${toSeoUrl(
                                            ticket.movie_title,
                                        )}`,
                                    }}
                                >
                                    {ticket.movie_title}
                                </Link>
                            </div>
                            <div className="ticket-history__block">
                                <SessionDetails
                                    session={{
                                        ticket_class_id: ticket.ticket_class_id,
                                        session_date: ticket.sessionDate,
                                        venue_name: ticket.venue_name,
                                        venue_address: ticket.venue_address,
                                        venue_suburb: ticket.venue_suburb,
                                        venue_state: ticket.venue_state,
                                    }}
                                />
                                <p className="ticket-history__purchase-date">
                                    Purchase date: {ticket.purchase_date ? format(new Date(ticket.purchase_date), "do MMMM yyyy") : ""}
                                </p>
                            </div>
                            <div className="ticket-history__block">
                                <BookingDetails ticket={ticket} />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="ticket-history__content-wrapper">
                                <div className="ticket-history__block">
                                    <div className="ticket-history__text">
                                        {new Date(ticket.purchase_date).toLocaleDateString()}
                                    </div>
                                    <div className="ticket-history__text">
                                        {ticket.items[0].cinema_group_name}
                                    </div>
                                    <BookingDetails ticket={ticket} />
                                </div>
                            </div>
                        </>
                    )}
                </>
                <div className="ticket-history__vouchers-container">
                    <Button
                        additionalClasses="button button__primary ticket-history__btn"
                        onClick={() => sendTicketEmail(ticket.uuid)}
                        disabled={processing === true && ticket.ticket_id === id}
                    >
                        {loading === true && ticket.ticket_id === id ? (
                            <Spinner text="loading..." />
                        ) : (
                            <span>Resend Email</span>
                        )}
                    </Button>

                    {error && ticket.ticket_id === id && (
                        <InlineMessage message={errorMessage} messageType="error" />
                    )}

                    {emailSent === true && (
                        <InlineMessage message={message} messageType="success" />
                    )}

                    {isV2tBooking && (
                        v2tDetails?.cinema_barcode_url ? (
                            <Link
                                className="button button__primary ticket-history__v2t-btn"
                                to={`/success_book_with_cinema?id=${ticket?.uuid}`}
                                state={{ from: location.pathname + location.search }}
                            >
                                {v2tDetails?.cinema_barcode_url
                                    ? <>
                                        <SeatIcon className="button__icon" />
                                        <strong>Seats:</strong>
                                        {v2tDetails?.cinema_seat}
                                    </>
                                    : "Booking In Progress"
                                }
                            </Link>
                        ) : (
                            <Link
                                className="button button__primary ticket-history__btn"
                                to={`/success_book_with_cinema?id=${ticket?.uuid}`}
                                state={{ from: location.pathname + location.search }}
                            >
                                Booking In Progress
                            </Link>
                        )
                    )}

                    {ticket.vouchers.length > 0 ? (
                        <VoucherList
                            vouchers={ticket.vouchers}
                            defaultDisplayLimit={defaultDisplayLimit}
                            isV2tBooking={isV2tBooking}
                        />
                    ) : ticket.qr_code_url ? (
                        <div className="ticket-history__code">
                            <img
                                className="img ticket-history__img"
                                alt="image of qr code"
                                src={ticket.qr_code_url}
                            />
                        </div>
                    ) : (
                        <div className="ticket-history__code">
                            <img
                                className="img ticket-history__img"
                                alt="image of bar code"
                                src={ticket.bar_code_url}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Ticket;
